/**
 * What a form field shows while the programme is read-only: its value as plain
 * text, with none of the chrome that says it could be changed.
 *
 * An `output`, because it is labelable, so the field's `<label for>` still
 * names it. `aria-live="off"` undoes the live region `output` carries.
 *
 * @module components/ui/Form/ReadOnlyValue
 */

import React from "react";

export interface ReadOnlyValueProps {
  /** The field's id, so its label still points here. */
  id: string;
  /** The text to show; empty reads as "Not set". */
  value: string;
  className?: string;
  "data-testid"?: string;
  "aria-describedby"?: string;
  "aria-label"?: string;
  ref?: React.Ref<HTMLOutputElement>;
}

export function ReadOnlyValue({
  id,
  value,
  className = "",
  "data-testid": testId,
  "aria-describedby": describedBy,
  "aria-label": ariaLabel,
  ref = undefined,
}: ReadOnlyValueProps): React.ReactElement {
  const empty = value.trim() === "";
  return (
    <output
      ref={ref}
      id={id}
      // Programmatically focusable only, so a lock that closes over the focused
      // field has somewhere to put focus without adding a tab stop per value.
      tabIndex={-1}
      aria-live="off"
      aria-describedby={describedBy}
      aria-label={ariaLabel}
      data-testid={testId}
      data-read-only-value
      className={`block min-h-6 text-sm leading-6 break-words whitespace-pre-wrap ${
        empty ? "text-muted" : "text-base-content"
      } ${className}`.trim()}
    >
      {empty ? "Not set" : value}
    </output>
  );
}
